import App from './App.svelte';
import { mount as mountComponent, unmount } from 'svelte';

type WidgetProps = Record<string, unknown>;

export type WidgetConfig = {
  target?: string | HTMLElement;
  props?: WidgetProps;
  autoMount?: boolean;
};

type WidgetInstance = Record<string, any>;

const mounted = new Set<WidgetInstance>();

function resolveTarget(target?: string | HTMLElement) {
  if (!target) {
    const host = document.createElement('div');
    host.id = 'webtek-chat-widget-host';
    host.style.position = 'relative';
    const parent = document.body ?? document.documentElement;
    if (!parent) {
      throw new Error('Cannot mount Webtek chat widget before the document is ready.');
    }
    parent.appendChild(host);
    return host;
  }

  if (typeof target === 'string') {
    const element = document.querySelector(target);
    if (!element) {
      throw new Error(`Webtek chat widget could not find target "${target}".`);
    }
    return element as HTMLElement;
  }

  return target;
}

export function mount(config: WidgetConfig = {}) {
  const target = resolveTarget(config.target);
  const app = mountComponent(App, {
    target,
    props: {
      embedded: true,
      ...(config.props ?? {})
    } as WidgetProps
  });

  mounted.add(app);
  return app;
}

export async function destroyAll(options?: { outro?: boolean }) {
  await Promise.all([...mounted].map((instance) => unmount(instance, options)));
  mounted.clear();
}

function autoMountFromGlobal(config?: WidgetConfig) {
  const boot = () => {
    try {
      mount(config);
    } catch (error) {
      console.error('Failed to initialize Webtek chat widget', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}

function envString(value: string | boolean | undefined) {
  return typeof value === 'string' ? value : undefined;
}

const widgetVersion =
  envString(import.meta.env.VITE_WIDGET_VERSION) ??
  envString(import.meta.env.VITE_APP_VERSION) ??
  envString(import.meta.env.VITE_COMMIT_SHA) ??
  'dev';

declare global {
  interface Window {
    WebtekChatWidgetConfig?: WidgetConfig;
    WebtekChatWidget?: {
      mount: typeof mount;
      destroyAll: typeof destroyAll;
      version: string;
    };
  }
}

if (typeof window !== 'undefined') {
  window.WebtekChatWidget = {
    mount,
    destroyAll,
    version: widgetVersion
  };

  const config = window.WebtekChatWidgetConfig;
  if (!config || config.autoMount !== false) {
    autoMountFromGlobal(config);
  }
}
